import { FeatureGate } from '../components/FeatureGate';
import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isValidSlug, slugify } from '@aiai/shared';
import { leaveOrg, updateOrg } from '../api';
import { errorMessage } from '../lib/api';
import { useOrg } from '../org/useOrg';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { FormField } from '../components/FormField';
import { Flash } from '../components/Flash';
import { Callout } from '../components/Callout';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { WorkspaceDomainCard } from '../components/WorkspaceDomainCard';
import { IntegrationTokensCard } from '../components/IntegrationTokensCard';
import { PlanUsagePanel } from '../components/PlanUsagePanel';
import { WorkspaceSwitcher } from '../components/WorkspaceSwitcher';
import { MembersSettingsSection } from './MembersSettingsSection';

/**
 * Workspace-scoped settings (`/w/:slug/settings`): rename the workspace and its
 * URL slug, the workspace prototype domain and Integration tokens (owner only),
 * plus manage members. Account-level settings (email,
 * password, MCP connections, notifications) live at the global `/settings`.
 */
export function WorkspaceSettingsPage() {
  useDocumentTitle('Workspace settings');
  const { activeOrg } = useOrg();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isOwner = activeOrg?.role === 'owner';

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-lg font-bold text-ink">Workspace settings</h1>
        <p className="mt-1 text-sm text-ink-muted">
          {isOwner
            ? 'Rename this workspace and manage who can access it.'
            : 'Workspace configuration is managed by the workspace owner.'}
        </p>
      </div>

      <WorkspaceSwitcher subPath="settings" />

      {activeOrg && <PlanUsagePanel orgId={activeOrg.id} />}

      {isOwner && activeOrg && (
        <RenameForm
          key={activeOrg.id}
          orgId={activeOrg.id}
          currentName={activeOrg.name}
          currentSlug={activeOrg.slug}
          onRenamed={(slug) => {
            // OrgContext caches the workspace list — refetch so the new
            // name/slug propagate to the nav and switcher.
            queryClient.invalidateQueries({ queryKey: ['orgs'] });
            if (slug !== activeOrg.slug) {
              navigate(`/w/${slug}/settings`, { replace: true });
            }
          }}
        />
      )}

      {isOwner && activeOrg && (
        <FeatureGate feature="workspacePrototypeDomain">
          <WorkspaceDomainCard orgId={activeOrg.id} />
        </FeatureGate>
      )}

      {/* Workspace (prototype-factory) Integration API tokens — owner-only (workspace:manage). */}
      {isOwner && (
        <FeatureGate feature="integrationTokens">
          <IntegrationTokensCard />
        </FeatureGate>
      )}

      {isOwner && <MembersSettingsSection />}

      <LeaveWorkspaceSection />
    </div>
  );
}

/**
 * Leave the current workspace. Any member can, and so can an owner while
 * another owner remains; the last owner can't (the API rejects it — surfaced
 * in the dialog). On success we switch to another of the
 * user's workspaces so they're never stranded on a workspace they just left.
 */
function LeaveWorkspaceSection() {
  const { activeOrg, orgs, setActiveOrg } = useOrg();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState(false);

  const leave = useMutation({
    mutationFn: () => leaveOrg(activeOrg!.id),
    onSuccess: () => {
      const remaining = orgs.find((o) => o.id !== activeOrg!.id);
      queryClient.invalidateQueries({ queryKey: ['orgs'] });
      setConfirm(false);
      if (remaining) {
        setActiveOrg(remaining.id);
        navigate(`/w/${remaining.slug}`, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    },
  });

  if (!activeOrg) return null;
  const isOwner = activeOrg.role === 'owner';

  return (
    <section className="space-y-2 border-t border-line pt-6">
      <h2 className="text-sm font-semibold text-ink">Leave workspace</h2>
      <p className="text-sm text-ink-muted">
        Remove yourself from{' '}
        <span className="font-medium text-ink">{activeOrg.name}</span>. You’ll
        lose access to its projects and runs, and can only rejoin with a new
        invitation.
        {isOwner && (
          <>
            {' '}As an owner you can leave only while another owner remains —
            make another member an owner first.
          </>
        )}
      </p>
      <button
        type="button"
        className="btn-secondary text-danger"
        onClick={() => setConfirm(true)}
      >
        Leave workspace
      </button>
      <ConfirmDialog
        open={confirm}
        title="Leave workspace?"
        confirmLabel="Leave workspace"
        busy={leave.isPending}
        error={leave.isError ? errorMessage(leave.error, 'Could not leave the workspace') : null}
        onConfirm={() => leave.mutate()}
        onClose={() => setConfirm(false)}
        message={
          <>
            Remove yourself from{' '}
            <span className="font-medium text-ink">{activeOrg.name}</span>?
          </>
        }
      />
    </section>
  );
}

function RenameForm({
  orgId,
  currentName,
  currentSlug,
  onRenamed,
}: {
  orgId: string;
  currentName: string;
  currentSlug: string;
  onRenamed: (slug: string) => void;
}) {
  const [name, setName] = useState(currentName);
  const [flash, setFlash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The slug is DERIVED from the name (read-only) and updates live as you type,
  // so a bad URL can't be hand-typed. Validated with the SAME shared rules the
  // API enforces (reserved/offensive/format) before submit.
  const slug = slugify(name);

  const save = useMutation({
    mutationFn: () => updateOrg(orgId, { name: name.trim(), slug }),
    onSuccess: (org) => {
      setError(null);
      setFlash('Workspace updated');
      setName(org.name);
      onRenamed(org.slug);
    },
    onError: (err) => {
      setFlash(null);
      setError(errorMessage(err, 'Could not update the workspace'));
    },
  });

  const nameValid = name.trim().length > 0;
  const slugValid = isValidSlug(slug);
  const dirty = name.trim() !== currentName || slug !== currentSlug;
  const canSave = dirty && slugValid && nameValid && !save.isPending;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    save.mutate();
  };

  return (
    <form onSubmit={onSubmit} className="card space-y-4 p-4">
      {flash && <Flash>{flash}</Flash>}
      {error && <Callout tone="danger">{error}</Callout>}
      <FormField label="Workspace name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          className="input"
          placeholder="Acme Inc"
        />
      </FormField>
      <FormField label="Workspace URL">
        <div>
          <div className="flex items-center gap-1">
            <span className="whitespace-nowrap text-sm text-ink-faint">/w/</span>
            <input
              value={slug}
              readOnly
              aria-readonly="true"
              tabIndex={-1}
              className="input font-mono cursor-not-allowed bg-surface-2 text-ink-muted"
              placeholder="acme-inc"
            />
          </div>
          {nameValid && !slugValid ? (
            <p className="mt-1 text-xs text-danger">
              That name makes a reserved or restricted URL — pick a different name.
            </p>
          ) : (
            <p className="mt-1 text-xs text-ink-faint">
              Generated from the name. Changing it breaks existing links to this workspace.
            </p>
          )}
        </div>
      </FormField>
      <div className="flex justify-end">
        <button type="submit" disabled={!canSave} className="btn-primary">
          {save.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  );
}
